/**
 * UsageIndicator — T3.18 / PS4.
 *
 * Progressive plan-limit surfacing in the editor toolbar:
 *
 *   < 50% storage           → hidden (no noise)
 *   50-80%                  → subtle low-emphasis text "402 / 500 MB"
 *   80-95%                  → soft amber banner-style text
 *   95-100%                 → red, with strong contrast
 *   = / > 100%              → red + bold; uploads are hard-blocked
 *                             upstream by uploadAsset, this just makes
 *                             the state legible
 *
 * Sites count uses the same thresholds against `plan.maxSitesPerUser`
 * so users near the cap see a hint without being yelled at.
 *
 * Returns null when plan/usage info is missing (builder-api was
 * unreachable when /api/me ran) — graceful degradation.
 */
import { css, html } from "react-strict-dom";
import { Tooltip } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { radii, spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import type { MeResponse } from "../api/client.js";

const MB = 1024 * 1024;

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < MB) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * MB) return `${(b / MB).toFixed(0)} MB`;
  return `${(b / 1024 / MB).toFixed(1)} GB`;
}

const styles = css.create({
  root: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    paddingBlock: spacing.xs,
    paddingInline: spacing.sm,
    borderRadius: radii.xs,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: "transparent",
    backgroundColor: "transparent",
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
    fontVariantNumeric: "tabular-nums",
  },
  warning: {
    backgroundColor: colors.warningBg,
    borderColor: colors.warningBorder,
    color: colors.warningText,
  },
  error: {
    backgroundColor: colors.errorBg,
    borderColor: colors.errorBorder,
    color: colors.errorText,
  },
  separator: {
    opacity: 0.5,
  },
});

/** ≥ 95% → error tone, ≥ 80% → warning tone, below → the muted base. */
function thresholdStyle(pct: number) {
  if (pct >= 95) return styles.error;
  if (pct >= 80) return styles.warning;
  return null;
}

export function UsageIndicator({ me }: { me: MeResponse }) {
  const plan = me.plan;
  const usage = me.usage;
  if (!plan || !usage) return null;

  const storagePct = plan.storageLimitBytes
    ? (usage.storageBytes / plan.storageLimitBytes) * 100
    : 0;
  const sitesPct = plan.maxSitesPerUser
    ? (usage.sitesCount / plan.maxSitesPerUser) * 100
    : 0;

  // PS4: hide entirely below 50% to keep the toolbar quiet.
  if (storagePct < 50 && sitesPct < 50) return null;

  const showStorage = storagePct >= 50;
  const showSites = sitesPct >= 50;
  const worstPct = Math.max(storagePct, sitesPct);
  const tone = thresholdStyle(worstPct);

  return (
    <Tooltip.Root
      content={`Plan: ${plan.tier} — ${formatBytes(usage.storageBytes)} / ${formatBytes(plan.storageLimitBytes)} storage, ${usage.sitesCount} / ${plan.maxSitesPerUser} sites`}
      placement="bottom"
    >
      <Tooltip.Trigger>
        <html.div style={[styles.root, tone]}>
          {showStorage && (
            <html.span>
              {formatBytes(usage.storageBytes)} /{" "}
              {formatBytes(plan.storageLimitBytes)}
            </html.span>
          )}
          {showStorage && showSites && (
            <html.span style={styles.separator}>·</html.span>
          )}
          {showSites && (
            <html.span>
              {usage.sitesCount} / {plan.maxSitesPerUser} sites
            </html.span>
          )}
        </html.div>
      </Tooltip.Trigger>
    </Tooltip.Root>
  );
}
